import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../../services/api';
import { useAdmin } from '../../../contexts/AdminContext';
import UsersList from './UsersList';
import UserForm from './UserForm';
import UserStats from './UserStats';

function AdminUsers() {
  const { user: currentUser, isAuthenticated } = useAdmin();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [editingUser, setEditingUser] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    role: '',
    is_active: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    console.log('AdminUsers useEffect - isAdmin:', isAdmin, 'currentUser:', currentUser);
    if (isAdmin) {
      loadUsers();
      loadStats();
    }
  }, [filters, isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('Loading users with filters:', filters);
      console.log('Current user:', currentUser);
      console.log('Token in localStorage:', !!localStorage.getItem('cms_token'));
      
      // Clean filters - remove empty strings to avoid backend issues
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      );
      console.log('Clean filters:', cleanFilters);
      
      const response = await usersAPI.getAll(cleanFilters);
      console.log('Users API response:', response);
      console.log('Response structure:', JSON.stringify(response, null, 2));
      console.log('Users data:', response.data);
      console.log('Users data structure:', JSON.stringify(response.data, null, 2));
      console.log('Users array length:', response.data?.data?.length);
      
      if (response.data && response.data.data) {
        setUsers(response.data.data);
        setPagination(response.data.pagination || { page: 1, limit: 10, total: 0, pages: 1 });
      } else {
        console.error('Unexpected API response structure:', response);
        setUsers([]);
      }
      setError('');
    } catch (err) {
      console.error('Error loading users:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await usersAPI.getStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setActiveTab('form');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setActiveTab('form');
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      try {
        await usersAPI.delete(userId);
        setSuccess(`User "${username}" has been deleted successfully`);
        loadUsers();
        loadStats();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete user');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleUserSaved = (message) => {
    setSuccess(message);
    setActiveTab('list');
    loadUsers();
    loadStats();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 3000);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
  };

  // Temporarily show debug info and bypass admin check for debugging
  if (!isAuthenticated) {
    return (
      <div className="admin-users">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Not authenticated. Please login first.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-users">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Access denied. Admin privileges required to manage users.
                <br />
                <small>Current role: {currentUser?.role || 'undefined'}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="page-header">
              <div className="page-title">
                <h1>
                  <i className="fas fa-users me-3"></i>
                  User Management
                </h1>
                <p className="text-muted">Manage admin accounts and permissions</p>
              </div>
              <div className="page-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleCreateUser}
                  disabled={loading}
                >
                  <i className="fas fa-plus me-2"></i>
                  Add New User
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="alert alert-success alert-dismissible">
                <i className="fas fa-check-circle me-2"></i>
                {success}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess('')}
                ></button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="alert alert-danger alert-dismissible">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError('')}
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                  onClick={() => setActiveTab('list')}
                >
                  <i className="fas fa-list me-2"></i>
                  Users List
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'stats' ? 'active' : ''}`}
                  onClick={() => setActiveTab('stats')}
                >
                  <i className="fas fa-chart-bar me-2"></i>
                  Statistics
                </button>
              </li>
              {activeTab === 'form' && (
                <li className="nav-item">
                  <button className="nav-link active">
                    <i className="fas fa-user-edit me-2"></i>
                    {editingUser ? 'Edit User' : 'Add User'}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Tab Content */}
        <div className="row">
          <div className="col-12">
            {activeTab === 'list' && (
              <UsersList
                users={users}
                loading={loading}
                filters={filters}
                pagination={pagination}
                onFilterChange={handleFilterChange}
                onPageChange={handlePageChange}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                currentUser={currentUser}
              />
            )}

            {activeTab === 'stats' && (
              <UserStats stats={stats} />
            )}

            {activeTab === 'form' && (
              <UserForm
                user={editingUser}
                onSave={handleUserSaved}
                onError={handleError}
                onCancel={() => setActiveTab('list')}
              />
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-users {
          padding: 2rem 0;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          padding: 1.5rem 0;
          border-bottom: 2px solid #e9ecef;
        }

        .page-title h1 {
          color: #495057;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .page-title p {
          margin-bottom: 0;
          font-size: 1.1rem;
        }

        .page-actions .btn {
          font-weight: 500;
        }

        .nav-tabs {
          border-bottom: 2px solid #dee2e6;
          margin-bottom: 0;
        }

        .nav-tabs .nav-link {
          border: none;
          background: none;
          color: #6c757d;
          font-weight: 500;
          padding: 1rem 1.5rem;
          border-bottom: 3px solid transparent;
          transition: all 0.3s ease;
        }

        .nav-tabs .nav-link:hover {
          background-color: #f8f9fa;
          color: #495057;
          border-bottom-color: #dee2e6;
        }

        .nav-tabs .nav-link.active {
          color: #c9302c;
          border-bottom-color: #c9302c;
          background-color: #fff;
        }

        .alert {
          border: none;
          border-radius: 8px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }

        .alert-success {
          background-color: #d1edff;
          color: #0c5460;
        }

        .alert-danger {
          background-color: #f8d7da;
          color: #721c24;
        }

        .alert-warning {
          background-color: #fff3cd;
          color: #856404;
        }

        .btn-close {
          filter: none;
          opacity: 0.7;
        }

        .btn-close:hover {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .page-actions {
            margin-top: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminUsers;